import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, Download, Printer, FileSpreadsheet, File, 
  Users, Vote, Trophy, TrendingUp, BarChart3, PieChart as PieChartIcon,
  Building, Calendar, MapPin
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Party-specific colors
const PARTY_COLORS = {
  'BJP': '#FF9933',
  'INC': '#19AAED',
  'NCP': '#00BFFF',
  'SS': '#FF6600',
  'SS-UBT': '#F97316',
  'BSP': '#22C55E',
  'AIMIM': '#22C55E',
  'IUML': '#138808',
  'MNS': '#FF6600',
  'NCP-SP': '#00CED1',
};

const DEFAULT_COLORS = ['#6366F1', '#EC4899', '#8B5CF6', '#14B8A6', '#F59E0B', '#EF4444', '#3B82F6'];

const getPartyColor = (partyName, index) => {
  return PARTY_COLORS[partyName] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
};

const Report = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [zoneSummary, setZoneSummary] = useState([]);
  const [pollingData, setPollingData] = useState([]);
  const [reportType, setReportType] = useState('summary');
  const reportRef = useRef(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [statsRes, candidatesRes, zoneRes, pollingRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/elected-candidates`),
        axios.get(`${API}/zone-summary`),
        axios.get(`${API}/polling-percentage`)
      ]);
      setStats(statsRes.data);
      setCandidates(candidatesRes.data);
      setZoneSummary(zoneRes.data);
      setPollingData(pollingRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to sanitize text for PDF (replace Marathi with English equivalent)
  const sanitizeForPDF = (text) => {
    if (!text) return '-';
    const marathiToEnglish = {
      'भारतीय जनता पार्टी': 'Bhartiya Janata Party (BJP)',
      'इंडियन नॅशनल काँग्रेस': 'Indian National Congress (INC)',
      'राष्ट्रवादी काँग्रेस पार्टी': 'Nationalist Congress Party (NCP)',
      'शिवसेना': 'Shivsena',
      'बहुजन समाज पार्टी': 'Bahujan Samaj Party (BSP)',
    };
    // Check if text contains Marathi characters (Devanagari Unicode range)
    if (/[\u0900-\u097F]/.test(text)) {
      return marathiToEnglish[text] || text.replace(/[\u0900-\u097F]/g, '');
    }
    return text;
  };

  // Download as PDF
  const downloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(0, 51, 102);
    doc.text('Nagpur Municipal Corporation', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(16);
    doc.text('General Election Results 2025-26', pageWidth / 2, 30, { align: 'center' });
    
    // Date
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 38, { align: 'center' });
    
    // Summary Stats
    doc.setFontSize(14);
    doc.setTextColor(0, 51, 102);
    doc.text('Election Summary', 14, 50);
    
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text(`Total Voters: ${stats?.total_voters?.toLocaleString() || 0}`, 14, 60);
    doc.text(`Total Seats: ${stats?.total_seats || 0}`, 14, 68);
    doc.text(`Polling Stations: ${stats?.total_polling_stations?.toLocaleString() || 0}`, 14, 76);
    doc.text(`Average Polling: ${stats?.avg_polling_percentage || 0}%`, 14, 84);
    
    // Party-wise Seats
    doc.setFontSize(14);
    doc.setTextColor(0, 51, 102);
    doc.text('Party-wise Seats Won', 14, 100);
    
    const partyData = stats?.party_seats ? 
      Object.entries(stats.party_seats)
        .sort((a, b) => b[1] - a[1])
        .map(([party, seats]) => [sanitizeForPDF(party), seats.toString()]) 
      : [];
    
    autoTable(doc, {
      startY: 105,
      head: [['Party', 'Seats Won']],
      body: partyData,
      theme: 'striped',
      headStyles: { fillColor: [0, 51, 102] },
      margin: { left: 14 }
    });
    
    // Zone-wise Summary
    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(0, 51, 102);
    doc.text('Zone-wise Results Summary', 14, 20);
    
    const zoneData = zoneSummary.map(zone => [
      zone.zone,
      zone.total_seats.toString(),
      zone.total_voters.toLocaleString(),
      `${zone.avg_polling_percentage}%`,
      sanitizeForPDF(Object.keys(zone.party_distribution).sort((a, b) => 
        zone.party_distribution[b] - zone.party_distribution[a]
      )[0] || '-')
    ]);
    
    autoTable(doc, {
      startY: 25,
      head: [['Zone', 'Seats', 'Voters', 'Polling %', 'Leading Party']],
      body: zoneData,
      theme: 'striped',
      headStyles: { fillColor: [0, 51, 102] }
    });
    
    // All Elected Candidates
    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(0, 51, 102);
    doc.text(`All Elected Candidates (${candidates.length} Total)`, 14, 20);
    
    const allCandidates = candidates.map(c => [
        c.zone || '-',
        `${c.ward_no}-${c.seat_no}`,
        sanitizeForPDF(c.winner_name),
        sanitizeForPDF(c.winner_party),
        c.winner_votes.toLocaleString(),
        c.margin.toLocaleString()
      ]);
    
    autoTable(doc, {
      startY: 25,
      head: [['Zone', 'Ward-Seat', 'Winner Name', 'Party', 'Votes', 'Margin']],
      body: allCandidates,
      theme: 'striped',
      headStyles: { fillColor: [0, 51, 102] },
      styles: { fontSize: 7 }
    });
    
    doc.save('NMC_Election_Results_2025-26.pdf');
  };

  // Download as Excel
  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Summary Sheet
    const summaryData = [
      ['Nagpur Municipal Corporation - Election Results 2025-26'],
      ['नागपूर महानगरपालिका - निवडणूक निकाल 2025-26'],
      [''],
      ['Summary Statistics / सारांश आकडेवारी'],
      ['Total Voters / एकूण मतदार', stats?.total_voters || 0],
      ['Total Seats / एकूण जागा', stats?.total_seats || 0],
      ['Polling Stations / मतदान केंद्रे', stats?.total_polling_stations || 0],
      ['Average Polling % / सरासरी मतदान %', stats?.avg_polling_percentage || 0],
      [''],
      ['Party-wise Seats Won / पक्षनिहाय जिंकलेल्या जागा'],
      ['Party / पक्ष', 'Seats / जागा'],
      ...(stats?.party_seats ? Object.entries(stats.party_seats).sort((a, b) => b[1] - a[1]) : [])
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    
    // Zone Summary Sheet
    const zoneHeaders = ['Zone / झोन', 'Total Seats / एकूण जागा', 'Total Voters / एकूण मतदार', 'Polling % / मतदान %', 'Leading Party / अग्रणी पक्ष', 'BJP', 'INC', 'Others / इतर'];
    const zoneRows = zoneSummary.map(zone => [
      zone.zone,
      zone.total_seats,
      zone.total_voters,
      zone.avg_polling_percentage,
      Object.keys(zone.party_distribution).sort((a, b) => 
        zone.party_distribution[b] - zone.party_distribution[a]
      )[0] || '-',
      zone.party_distribution['BJP'] || 0,
      zone.party_distribution['INC'] || 0,
      Object.entries(zone.party_distribution)
        .filter(([p]) => p !== 'BJP' && p !== 'INC')
        .reduce((sum, [, count]) => sum + count, 0)
    ]);
    const zoneSheet = XLSX.utils.aoa_to_sheet([zoneHeaders, ...zoneRows]);
    XLSX.utils.book_append_sheet(workbook, zoneSheet, 'Zone Summary');
    
    // Elected Candidates Sheet
    const candidateHeaders = ['Sr.No / क्र.', 'Zone / झोन', 'Ward / प्रभाग', 'Seat / जागा', 'Reservation / आरक्षण', 'Winner Name / विजेता नाव', 'Winner Party / विजेता पक्ष', 'Winner Votes / विजेता मते', 'Runner-up Name / उपविजेता नाव', 'Runner-up Party / उपविजेता पक्ष', 'Runner-up Votes / उपविजेता मते', 'Margin / मताधिक्य'];
    const candidateRows = candidates.map((c, idx) => [
      idx + 1,
      c.zone || '-',
      c.ward_no,
      c.seat_no,
      c.reservation,
      c.winner_name,
      c.winner_party,
      c.winner_votes,
      c.runner_up_name,
      c.runner_up_party,
      c.runner_up_votes,
      c.margin
    ]);
    const candidateSheet = XLSX.utils.aoa_to_sheet([candidateHeaders, ...candidateRows]);
    XLSX.utils.book_append_sheet(workbook, candidateSheet, 'Elected Candidates');
    
    // Polling Data Sheet
    const pollingHeaders = ['Sr.No', 'Zone', 'Prabhag', 'Total Voters', '9:30 AM %', '11:30 AM %', '1:30 PM %', '3:30 PM %', '5:30 PM %'];
    const pollingRows = pollingData.map((p, idx) => [
      idx + 1,
      p.zone || p.mc_name,
      p.prabhag_no,
      p.total_voters,
      p.slot_9_30_percent || 0,
      p.slot_11_30_percent || 0,
      p.slot_1_30_percent || 0,
      p.slot_3_30_percent || 0,
      p.slot_5_30_percent || 0
    ]);
    const pollingSheet = XLSX.utils.aoa_to_sheet([pollingHeaders, ...pollingRows]);
    XLSX.utils.book_append_sheet(workbook, pollingSheet, 'Polling Percentage');
    
    // Download
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'NMC_Election_Results_2025-26.xlsx');
  };

  // Download as CSV with UTF-8 BOM for proper Marathi display
  const downloadCSV = (dataType) => {
    // Add UTF-8 BOM for Excel compatibility with Marathi
    const BOM = '\uFEFF';
    let csvContent = BOM;
    let filename = '';
    
    switch (dataType) {
      case 'candidates':
        csvContent += 'Sr.No / क्र.,Zone / झोन,Ward / प्रभाग,Seat / जागा,Reservation / आरक्षण,Winner Name / विजेता नाव,Winner Party / विजेता पक्ष,Winner Votes / विजेता मते,Runner-up Name / उपविजेता नाव,Runner-up Party / उपविजेता पक्ष,Runner-up Votes / उपविजेता मते,Margin / मताधिक्य\n';
        candidates.forEach((c, idx) => {
          csvContent += `${idx + 1},"${c.zone || '-'}","${c.ward_no}","${c.seat_no}","${c.reservation}","${c.winner_name}","${c.winner_party}",${c.winner_votes},"${c.runner_up_name}","${c.runner_up_party}",${c.runner_up_votes},${c.margin}\n`;
        });
        filename = 'NMC_Elected_Candidates.csv';
        break;
      case 'zones':
        csvContent += 'Zone / झोन,Total Seats / एकूण जागा,Total Voters / एकूण मतदार,Polling % / मतदान %,Leading Party / अग्रणी पक्ष\n';
        zoneSummary.forEach(zone => {
          const leadingParty = Object.keys(zone.party_distribution).sort((a, b) => 
            zone.party_distribution[b] - zone.party_distribution[a]
          )[0] || '-';
          csvContent += `"${zone.zone}",${zone.total_seats},${zone.total_voters},${zone.avg_polling_percentage},"${leadingParty}"\n`;
        });
        filename = 'NMC_Zone_Summary.csv';
        break;
      case 'polling':
        csvContent += 'Zone / झोन,Prabhag / प्रभाग,Total Voters / एकूण मतदार,9:30 AM %,11:30 AM %,1:30 PM %,3:30 PM %,5:30 PM %\n';
        pollingData.forEach(p => {
          csvContent += `"${p.zone || p.mc_name}","${p.prabhag_no}",${p.total_voters},${p.slot_9_30_percent || 0},${p.slot_11_30_percent || 0},${p.slot_1_30_percent || 0},${p.slot_3_30_percent || 0},${p.slot_5_30_percent || 0}\n`;
        });
        filename = 'NMC_Polling_Percentage.csv';
        break;
      default:
        return;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
  };

  // Print Report
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div className="text-center py-12">{language === 'en' ? 'Loading...' : 'लोड होत आहे...'}</div>;
  }

  // Chart data
  const partyChartData = stats?.party_seats ? 
    Object.entries(stats.party_seats)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value) 
    : [];

  const zoneChartData = zoneSummary.map(zone => ({
    name: zone.zone.replace('Zone ', 'Z'),
    seats: zone.total_seats,
    polling: zone.avg_polling_percentage,
  }));

  return (
    <div className="space-y-6" data-testid="report-page">
      {/* Header with Download Options */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className={`text-3xl font-bold text-primary mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
            {language === 'en' ? 'Election Report' : 'निवडणूक अहवाल'}
          </h1>
          <p className={`text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
            {language === 'en' ? 'Comprehensive election results report with download options' : 'डाउनलोड पर्यायांसह सर्वसमावेशक निवडणूक निकाल अहवाल'}
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button onClick={downloadPDF} className="flex items-center gap-2" data-testid="download-pdf-btn">
            <File className="h-4 w-4" />
            {language === 'en' ? 'Download PDF' : 'PDF डाउनलोड'}
          </Button>
          <Button onClick={downloadExcel} variant="outline" className="flex items-center gap-2" data-testid="download-excel-btn">
            <FileSpreadsheet className="h-4 w-4" />
            {language === 'en' ? 'Download Excel' : 'Excel डाउनलोड'}
          </Button>
          <Button onClick={handlePrint} variant="outline" className="flex items-center gap-2" data-testid="print-btn">
            <Printer className="h-4 w-4" />
            {language === 'en' ? 'Print' : 'प्रिंट'}
          </Button>
        </div>
      </div>

      {/* CSV Download Options */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Download className="h-4 w-4" />
            {language === 'en' ? 'Download Individual Data (CSV)' : 'वैयक्तिक डेटा डाउनलोड करा (CSV)'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => downloadCSV('candidates')} data-testid="download-candidates-csv">
              {language === 'en' ? 'Elected Candidates' : 'निर्वाचित उमेदवार'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => downloadCSV('zones')} data-testid="download-zones-csv">
              {language === 'en' ? 'Zone Summary' : 'झोन सारांश'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => downloadCSV('polling')} data-testid="download-polling-csv">
              {language === 'en' ? 'Polling Percentage' : 'मतदान टक्केवारी'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      <div ref={reportRef} className="space-y-6 print:space-y-4">
        {/* Report Header */}
        <Card className="bg-primary/5 border-primary/20 print:bg-white">
          <CardContent className="py-6">
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <Building className="h-12 w-12 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">Nagpur Municipal Corporation</h2>
              <h3 className="text-xl text-muted-foreground">General Election Results 2025-26</h3>
              <div className="flex justify-center items-center gap-2 mt-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{language === 'en' ? 'Report Generated' : 'अहवाल तयार'}: {new Date().toLocaleDateString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                {language === 'en' ? 'Total Voters' : 'एकूण मतदार'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_voters?.toLocaleString() || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                <Vote className="h-4 w-4" />
                {language === 'en' ? 'Total Seats' : 'एकूण जागा'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_seats || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {language === 'en' ? 'Polling Stations' : 'मतदान केंद्रे'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_polling_stations?.toLocaleString() || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                {language === 'en' ? 'Avg Polling' : 'सरासरी मतदान'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.avg_polling_percentage || 0}%</div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:hidden">
          {/* Party Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChartIcon className="h-5 w-5 text-primary" />
                {language === 'en' ? 'Party-wise Seats Distribution' : 'पक्षनिहाय जागा वितरण'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={partyChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={100}
                    dataKey="value"
                  >
                    {partyChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getPartyColor(entry.name, index)} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Zone Comparison */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                {language === 'en' ? 'Zone-wise Statistics' : 'झोननिहाय आकडेवारी'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={zoneChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" orientation="left" stroke="#003366" />
                  <YAxis yAxisId="right" orientation="right" stroke="#FF9933" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="seats" fill="#003366" name={language === 'en' ? 'Seats' : 'जागा'} />
                  <Bar yAxisId="right" dataKey="polling" fill="#FF9933" name={language === 'en' ? 'Polling %' : 'मतदान %'} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Party-wise Seats Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-accent" />
              {language === 'en' ? 'Party-wise Seats Won' : 'पक्षनिहाय जिंकलेल्या जागा'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{language === 'en' ? 'Rank' : 'क्रमांक'}</TableHead>
                  <TableHead>{language === 'en' ? 'Party' : 'पक्ष'}</TableHead>
                  <TableHead>{language === 'en' ? 'Seats Won' : 'जिंकलेल्या जागा'}</TableHead>
                  <TableHead>{language === 'en' ? 'Percentage' : 'टक्केवारी'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partyChartData.map((party, index) => (
                  <TableRow key={party.name}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Badge style={{ backgroundColor: getPartyColor(party.name, index) }} className="text-white">
                        {party.name}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-bold">{party.value}</TableCell>
                    <TableCell>{((party.value / (stats?.total_seats || 1)) * 100).toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Zone Summary Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              {language === 'en' ? 'Zone-wise Summary' : 'झोननिहाय सारांश'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{language === 'en' ? 'Zone' : 'झोन'}</TableHead>
                  <TableHead>{language === 'en' ? 'Total Seats' : 'एकूण जागा'}</TableHead>
                  <TableHead>{language === 'en' ? 'Total Voters' : 'एकूण मतदार'}</TableHead>
                  <TableHead>{language === 'en' ? 'Polling %' : 'मतदान %'}</TableHead>
                  <TableHead>{language === 'en' ? 'Leading Party' : 'अग्रणी पक्ष'}</TableHead>
                  <TableHead>BJP</TableHead>
                  <TableHead>INC</TableHead>
                  <TableHead>{language === 'en' ? 'Others' : 'इतर'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {zoneSummary.map((zone) => {
                  const leadingParty = Object.keys(zone.party_distribution).sort((a, b) => 
                    zone.party_distribution[b] - zone.party_distribution[a]
                  )[0] || '-';
                  const others = Object.entries(zone.party_distribution)
                    .filter(([p]) => p !== 'BJP' && p !== 'INC')
                    .reduce((sum, [, count]) => sum + count, 0);
                  return (
                    <TableRow key={zone.zone}>
                      <TableCell className="font-medium">{zone.zone}</TableCell>
                      <TableCell>{zone.total_seats}</TableCell>
                      <TableCell>{zone.total_voters.toLocaleString()}</TableCell>
                      <TableCell>{zone.avg_polling_percentage}%</TableCell>
                      <TableCell>
                        <Badge style={{ backgroundColor: getPartyColor(leadingParty, 0) }} className="text-white">
                          {leadingParty}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge style={{ backgroundColor: '#FF9933' }} className="text-white">
                          {zone.party_distribution['BJP'] || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge style={{ backgroundColor: '#19AAED' }} className="text-white">
                          {zone.party_distribution['INC'] || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>{others}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Top Winners Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-accent" />
              {language === 'en' ? 'Top 20 Winners by Margin' : 'मताधिक्यानुसार शीर्ष 20 विजेते'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>{language === 'en' ? 'Zone' : 'झोन'}</TableHead>
                  <TableHead>{language === 'en' ? 'Ward-Seat' : 'प्रभाग-जागा'}</TableHead>
                  <TableHead>{language === 'en' ? 'Winner' : 'विजेता'}</TableHead>
                  <TableHead>{language === 'en' ? 'Party' : 'पक्ष'}</TableHead>
                  <TableHead>{language === 'en' ? 'Votes' : 'मते'}</TableHead>
                  <TableHead>{language === 'en' ? 'Margin' : 'मताधिक्य'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...candidates]
                  .sort((a, b) => b.margin - a.margin)
                  .slice(0, 20)
                  .map((candidate, index) => (
                    <TableRow key={candidate.id || index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{candidate.zone || '-'}</TableCell>
                      <TableCell>{candidate.ward_no}-{candidate.seat_no}</TableCell>
                      <TableCell className="font-medium">{candidate.winner_name}</TableCell>
                      <TableCell>
                        <Badge style={{ backgroundColor: getPartyColor(candidate.winner_party, 0) }} className="text-white text-xs">
                          {candidate.winner_party}
                        </Badge>
                      </TableCell>
                      <TableCell>{candidate.winner_votes.toLocaleString()}</TableCell>
                      <TableCell className="font-bold text-accent">{candidate.margin.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Report;
